import React from "react";
import Image from "@Components/Image";
import CountryFlag from "@Components/CountryFlag";
import useTranslationHook from "hooks/useTranslationHook";
import { ReviewData } from "../../types";
import { truncateText, getInitials, getNationalityCountryCode } from "../../utils";
import { isFallbackHeadingKey } from "../../buildReviewsData";
import useHelpfulClick from "../../hooks/useHelpfulClick";
import { trackHelpfulButtonClicked } from "../../analytics";
import styles from "./reviewCard.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";

interface ReviewCardProps {
  review: ReviewData;
  onMediaClick?: (index: number) => void;
  onReadMore?: (reviewId: string) => void;
  mediaStartIndex?: number;
  fullWidth?: boolean;
  showFullBody?: boolean;
  hideMedia?: boolean;
  propertyId?: string | number;
  isActive?: boolean;
}

const TRUNCATE_LIMITS = {
  withBoth: 195,
  withEither: 260,
  bodyOnly: 450,
};

const MOBILE_TRUNCATE_LIMITS = {
  withBoth: 80,
  withEither: 95,
  bodyOnly: 150,
};

function getMaxLength(
  roomType: string,
  dateRange: string,
  hasPhotos: boolean,
  limits: typeof TRUNCATE_LIMITS,
) {
  if ((roomType || dateRange) && hasPhotos) return limits.withBoth;
  if (roomType || dateRange || hasPhotos) return limits.withEither;
  return limits.bodyOnly;
}

const RoomIcon = () => (
  <svg
    className={styles.roomIcon}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M3.59941 5.39883C3.59941 5.06883 3.32941 4.79883 2.99941 4.79883C2.66941 4.79883 2.39941 5.06883 2.39941 5.39883V8.99883C2.39941 10.9863 4.01191 12.5988 5.99941 12.5988H19.5519L15.1757 16.9751C14.9432 17.2076 14.9432 17.5901 15.1757 17.8226C15.4082 18.0551 15.7907 18.0551 16.0232 17.8226L21.4232 12.4226C21.5357 12.3101 21.5994 12.1563 21.5994 11.9988C21.5994 11.8413 21.5357 11.6876 21.4232 11.5751L16.0232 6.17508C15.7907 5.94258 15.4082 5.94258 15.1757 6.17508C14.9432 6.40758 14.9432 6.79008 15.1757 7.02258L19.5519 11.3988H5.99941C4.67566 11.3988 3.59941 10.3226 3.59941 8.99883V5.39883Z"
      fill="#4B5563"
    />
  </svg>
);

const ThumbsUpIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M4.5 7V14H2.5C2.10218 14 1.72064 13.842 1.43934 13.5607C1.15804 13.2794 1 12.8978 1 12.5V8.5C1 8.10218 1.15804 7.72064 1.43934 7.43934C1.72064 7.15804 2.10218 7 2.5 7H4.5ZM4.5 7C5.56087 7 6.57828 6.57857 7.32843 5.82843C8.07857 5.07828 8.5 4.06087 8.5 3V2.5C8.5 2.10218 8.65804 1.72064 8.93934 1.43934C9.22064 1.15804 9.60218 1 10 1C10.3978 1 10.7794 1.15804 11.0607 1.43934C11.342 1.72064 11.5 2.10218 11.5 2.5V7H13.5C13.8978 7 14.2794 7.15804 14.5607 7.43934C14.842 7.72064 15 8.10218 15 8.5L14 12.5C13.8833 13.0051 13.6027 13.4559 13.2027 13.7849C12.8028 14.1138 12.3063 14.3021 11.787 14.32H7C6.60218 14.32 6.22064 14.162 5.93934 13.8807C5.65804 13.5994 5.5 13.2178 5.5 12.82V7.5"
      stroke="#1F2A37"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ThumbsUpFilledIcon = () => (
  <svg width="15" height="14" viewBox="0 0 15 14" fill="none" aria-hidden="true">
    <path
      d="M2.32258 4.18064C2.83645 4.18064 3.25161 4.59581 3.25161 5.10968V12.5419C3.25161 13.0558 2.83645 13.471 2.32258 13.471H0.929032C0.415161 13.471 0 13.0558 0 12.5419V5.10968C0 4.59581 0.415161 4.18064 0.929032 4.18064H2.32258ZM7.85613 0C8.64871 0 9.29032 0.641613 9.29032 1.43419V1.55613C9.29032 1.75355 9.25258 1.95097 9.18 2.13387L8.36129 4.18064H13.0065C13.7758 4.18064 14.4 4.80484 14.4 5.57419C14.4 6.14613 14.0545 6.63677 13.561 6.85161C14.0545 7.06645 14.4 7.5571 14.4 8.12903C14.4 8.80839 13.9123 9.37452 13.2677 9.49645C13.3955 9.70839 13.471 9.95516 13.471 10.2194C13.471 10.8639 13.0355 11.4039 12.4432 11.5635C12.5071 11.7232 12.5419 11.8974 12.5419 12.0774C12.5419 12.8468 11.9177 13.471 11.1484 13.471H8.59645C7.54258 13.471 6.51774 13.111 5.69613 12.4519L5.34194 12.1703C4.90064 11.819 4.64516 11.2848 4.64516 10.7187V5.30129C4.64516 4.86871 4.74677 4.44194 4.93839 4.05581L6.57 0.792581C6.81387 0.307742 7.31032 0 7.85613 0Z"
      fill="#1F2A37"
    />
  </svg>
);

const ChevronIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M5.13843 13.8872C5.04635 13.8031 5.00033 13.688 5.00033 13.5725C5.00033 13.4683 5.03799 13.3641 5.11416 13.2816L9.96461 8.00225L5.11405 2.71942C4.95335 2.54532 4.9634 2.27372 5.13832 2.11381C5.31242 1.9531 5.58266 1.96398 5.74336 2.13767L10.8857 7.70897C11.0381 7.87386 11.0381 8.12575 10.8857 8.29075L5.74336 13.8621C5.58196 14.0345 5.31252 14.0479 5.13843 13.8872Z"
      fill="#ED3A56"
    />
  </svg>
);

const ReviewCard: React.FC<ReviewCardProps> = ({
  review,
  onMediaClick,
  onReadMore,
  mediaStartIndex = 0,
  fullWidth = false,
  showFullBody = false,
  hideMedia = false,
  propertyId,
  isActive = false,
}) => {
  const { title, rating, roomType, dateRange, body, photos, helpfulCount, bookingDate, reviewer } =
    review;
  const { t } = useTranslationHook();
  const { isClicked, handleClick, isLoading } = useHelpfulClick(
    propertyId || "",
    review.id,
    helpfulCount,
  );

  const onHelpfulClick = () => {
    if (propertyId) {
      trackHelpfulButtonClicked({
        reviewId: review.id,
        propertyId,
        isVerifiedStay: Boolean(review.bookingDate),
        priorVoteCount: helpfulCount,
      });
    }
    handleClick();
  };

  const trimmedBody = body?.trim() || "";
  const maxLen = getMaxLength(roomType, dateRange, photos?.length > 0, TRUNCATE_LIMITS);
  const truncatedBody = showFullBody ? trimmedBody : truncateText(trimmedBody, maxLen);
  const isTruncated = truncatedBody !== trimmedBody;

  // Translate title if it's a fallback heading key, otherwise use as-is (user content)
  const displayTitle = isFallbackHeadingKey(title) ? t(title) : title;

  // Resolve reviewer name/university with translation fallbacks
  const displayName = reviewer.name || (reviewer.nameKey ? t(reviewer.nameKey) : "");
  const displayUniversity =
    reviewer.university ||
    (reviewer.universityKey
      ? t(reviewer.universityKey, { property: reviewer.universityParam || "" })
      : "");

  return (
    <div
      className={`${styles.card}${fullWidth ? ` ${styles.fullWidth}` : ""}${
        isActive ? ` ${styles.activeCard}` : ""
      }`}
    >
      <div className={`${styles.content}${fullWidth ? ` ${styles.autoHeight}` : ""}`}>
        <div className={styles.innerContent}>
          <div>
            <div className={styles.header}>
              <h3 className={formatClassNames(styles.title, fullWidth && styles.fullWidthTitle)}>
                {displayTitle || t("propertypage.reviews.title")}
              </h3>
              {rating > 0 && (
                <div className={styles.ratingBadge}>
                  <span className={styles.ratingValue}>{rating.toFixed(1)}</span>
                  <span className={styles.ratingMax}>/5</span>
                </div>
              )}
            </div>

            {(roomType || dateRange) && (
              <div className={styles.roomDetails}>
                <RoomIcon />
                <span className={formatClassNames(styles.roomText, fullWidth && styles.fullWidth)}>
                  {roomType}
                  {roomType && dateRange ? " \u00B7 " : ""}
                  {dateRange ? `${t("propertypage.reviews.from")} ${dateRange}` : ""}
                </span>
              </div>
            )}
          </div>

          {truncatedBody && (
            <p className={styles.body}>
              {isTruncated && onReadMore ? (
                <>
                  <span>{`${truncatedBody.replace(/\.{3}$/, "")}.... `}</span>
                  <span
                    role="button"
                    tabIndex={0}
                    className={styles.readMoreBtn}
                    onClick={() => onReadMore(review.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") onReadMore(review.id);
                    }}
                    aria-label="Read more"
                  >
                    {t("searchpage.searchheadingcontent.readmore")}
                    <ChevronIcon />
                  </span>
                </>
              ) : (
                `${truncatedBody}`
              )}
            </p>
          )}

          {!hideMedia && photos && photos.length > 0 && (
            <div className={styles.photos}>
              {photos.map((photo, index) => (
                <div
                  key={index}
                  className={styles.photoWrapper}
                  role="button"
                  tabIndex={0}
                  onClick={() => onMediaClick?.(mediaStartIndex + index)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") onMediaClick?.(mediaStartIndex + index);
                  }}
                  aria-label={photo.isVideo ? "Play video" : "View photo"}
                >
                  <Image
                    src={photo.src}
                    alt={photo.alt}
                    className={styles.photoThumbnail}
                    width={64}
                    height={64}
                  />
                  {photo.isVideo && (
                    <div className={styles.videoOverlay}>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path d="M5 3L12 8L5 13V3Z" fill="#FFFFFF" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            className={`${styles.helpfulButton}${
              isClicked ? ` ${styles.helpfulButtonClicked}` : ""
            }`}
            aria-label={isClicked ? "Remove helpful" : "Helpful"}
            aria-pressed={isClicked}
            onClick={onHelpfulClick}
            disabled={isLoading}
          >
            {isClicked ? <ThumbsUpFilledIcon /> : <ThumbsUpIcon />}
            <span className={styles.helpfulText}>{t("propertypage.reviews.helpful")}</span>
          </button>
          {bookingDate && (
            <span className={styles.bookingDate}>
              {t("propertypage.reviews.bookedon")} {bookingDate}
            </span>
          )}
        </div>
      </div>

      <div className={fullWidth ? styles.reviewerFooterPanel : styles.reviewerFooter}>
        <div className={styles.reviewerAvatar}>
          {reviewer.avatar ? (
            <Image
              src={reviewer.avatar}
              alt={`${displayName} avatar`}
              className={styles.avatarImage}
              width={40}
              height={40}
            />
          ) : (
            <div className={styles.avatarInitials}>{getInitials(displayName || "A")}</div>
          )}
          {reviewer.nationality && getNationalityCountryCode(reviewer.nationality) && (
            <div className={styles.nationalityBadge}>
              <CountryFlag
                countryCode={getNationalityCountryCode(reviewer.nationality)}
                width={16}
                height={16}
              />
            </div>
          )}
        </div>
        <div className={styles.reviewerDetails}>
          <span className={styles.reviewerName}>
            {displayName || t("propertypage.reviews.anonymous")}
          </span>
          {fullWidth ? (
            <div className={styles.reviewerMetaRow}>
              {displayUniversity && (
                <span className={styles.reviewerMeta}>{displayUniversity}</span>
              )}
              {displayUniversity && reviewer.enrolmentStatus && (
                <span className={styles.reviewerMetaSeparator}>&nbsp;·&nbsp;</span>
              )}
              {reviewer.enrolmentStatus && (
                <span className={styles.reviewerEnrolment}>{reviewer.enrolmentStatus}</span>
              )}
            </div>
          ) : (
            (displayUniversity || reviewer.enrolmentStatus) && (
              <span className={styles.reviewerMeta}>
                {displayUniversity && displayUniversity.length > 35
                  ? displayUniversity
                  : [displayUniversity, reviewer.enrolmentStatus].filter(Boolean).join(" \u00B7 ")}
              </span>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default wrapperHOC(ReviewCard, {
  componentName: "ReviewCard",
  showForChina: true,
});

// Mobile variant — no fixed height, dashed separator before reviewer
interface MobileReviewCardProps {
  review: ReviewData;
  onMediaClick?: (index: number) => void;
  onReadMore?: (reviewId: string) => void;
  mediaStartIndex?: number;
  propertyId?: string | number;
}

export const MobileReviewCard: React.FC<MobileReviewCardProps> = ({
  review,
  onMediaClick,
  onReadMore,
  mediaStartIndex = 0,
  propertyId,
}) => {
  const { title, rating, roomType, dateRange, body, photos, helpfulCount, bookingDate, reviewer } =
    review;
  const { t } = useTranslationHook();
  const { isClicked, handleClick, isLoading } = useHelpfulClick(
    propertyId || "",
    review.id,
    helpfulCount,
  );

  const onHelpfulClick = () => {
    if (propertyId) {
      trackHelpfulButtonClicked({
        reviewId: review.id,
        propertyId,
        isVerifiedStay: Boolean(review.bookingDate),
        priorVoteCount: helpfulCount,
      });
    }
    handleClick();
  };

  const trimmedBody = body?.trim() || "";
  const maxLen = getMaxLength(roomType, dateRange, photos?.length > 0, MOBILE_TRUNCATE_LIMITS);
  const truncatedBody = truncateText(trimmedBody, maxLen);
  const isTruncated = truncatedBody !== trimmedBody;

  // Translate title if it's a fallback heading key, otherwise use as-is (user content)
  const displayTitle = isFallbackHeadingKey(title) ? t(title) : title;

  // Resolve reviewer name/university with translation fallbacks
  const displayName = reviewer.name || (reviewer.nameKey ? t(reviewer.nameKey) : "");
  const displayUniversity =
    reviewer.university ||
    (reviewer.universityKey
      ? t(reviewer.universityKey, { property: reviewer.universityParam || "" })
      : "");

  return (
    <div className={styles.mobileCard}>
      <div className={styles.mobileContent}>
        <div className={styles.mobileContentSubPart1}>
          <div className={styles.mobileContentSubPart2}>
            <div className={styles.header}>
              <div>
                <h3 className={styles.title}>{displayTitle || t("propertypage.reviews.title")}</h3>
                {(roomType || dateRange) && (
                  <div className={styles.roomDetails}>
                    <RoomIcon />
                    <span className={styles.roomText}>
                      {roomType}
                      {roomType && dateRange ? " \u00B7 " : ""}
                      {dateRange ? `${t("propertypage.reviews.from")} ${dateRange}` : ""}
                    </span>
                  </div>
                )}
              </div>
              {rating > 0 && (
                <div className={styles.ratingBadge}>
                  <span className={styles.ratingValue}>{rating.toFixed(1)}</span>
                  <span className={styles.ratingMax}>/5</span>
                </div>
              )}
            </div>

            {truncatedBody && (
              <p className={styles.mobileBody}>
                {isTruncated && onReadMore ? (
                  <>
                    <span>{`${truncatedBody.replace(/\.{3}$/, "")}.... `}</span>
                    <span
                      role="button"
                      tabIndex={0}
                      className={styles.readMoreBtn}
                      onClick={() => onReadMore(review.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") onReadMore(review.id);
                      }}
                      aria-label="Read more"
                    >
                      {t("searchpage.searchheadingcontent.readmore")}
                      <ChevronIcon />
                    </span>
                  </>
                ) : (
                  `${truncatedBody}`
                )}
              </p>
            )}
          </div>
          {photos && photos.length > 0 && (
            <div className={styles.photos}>
              {photos.map((photo, index) => (
                <div
                  key={index}
                  className={styles.photoWrapper}
                  role="button"
                  tabIndex={0}
                  onClick={() => onMediaClick?.(mediaStartIndex + index)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") onMediaClick?.(mediaStartIndex + index);
                  }}
                  aria-label={photo.isVideo ? "Play video" : "View photo"}
                >
                  <Image
                    src={photo.src}
                    alt={photo.alt}
                    className={styles.photoThumbnail}
                    width={64}
                    height={64}
                  />
                  {photo.isVideo && (
                    <div className={styles.videoOverlay}>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path d="M5 3L12 8L5 13V3Z" fill="#FFFFFF" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            className={`${styles.helpfulButton}${
              isClicked ? ` ${styles.helpfulButtonClicked}` : ""
            }`}
            aria-label={isClicked ? "Remove helpful" : "Helpful"}
            aria-pressed={isClicked}
            onClick={onHelpfulClick}
            disabled={isLoading}
          >
            {isClicked ? <ThumbsUpFilledIcon /> : <ThumbsUpIcon />}
            <span className={styles.helpfulText}>{t("propertypage.reviews.helpful")}</span>
          </button>
          {bookingDate && (
            <span className={styles.bookingDate}>
              {t("propertypage.reviews.bookedon")} {bookingDate}
            </span>
          )}
        </div>
      </div>

      <div style={{ width: "100%" }}>
        <div className={styles.mobileSeparator} />
        <div className={styles.mobileFooter}>
          <div className={styles.reviewerAvatar}>
            {reviewer.avatar ? (
              <Image
                src={reviewer.avatar}
                alt={`${displayName} avatar`}
                className={styles.avatarImage}
                width={42}
                height={42}
              />
            ) : (
              <div className={styles.avatarInitials}>{getInitials(displayName || "A")}</div>
            )}
            {reviewer.nationality && getNationalityCountryCode(reviewer.nationality) && (
              <div className={styles.nationalityBadge}>
                <CountryFlag
                  countryCode={getNationalityCountryCode(reviewer.nationality)}
                  width={16}
                  height={16}
                />
              </div>
            )}
          </div>
          <div className={styles.reviewerDetails}>
            <span className={styles.reviewerName}>
              {displayName || t("propertypage.reviews.anonymous")}
            </span>
            {(displayUniversity || reviewer.enrolmentStatus) && (
              <span className={styles.reviewerMeta}>
                {[displayUniversity, reviewer.enrolmentStatus].filter(Boolean).join(" \u00B7 ")}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
